import React from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { format, parse } from 'date-fns';
import {
  AvailabilityFormValues,
  AvailabilityFormSlotInput,
  AvailabilityNeedTypes,
  AvailabilityStatuses,
  AvailabilityWorkModels,
  AVAILABILITY_NEED_TYPE_METADATA,
  AVAILABILITY_STATUS_METADATA,
  AVAILABILITY_WORK_MODEL_METADATA,
} from '../../models';
import { AllocationBucketTypes, ALLOCATION_BUCKET_METADATA } from '@/features/Collaborators/Models';
import { ClientCombobox } from '@/components/shared/ClientCombobox';
import { CustomDatePicker } from '@/components/shared/inputs/CustomDatePicker';
import { useToast } from '@/hooks/useToast';
import { AvailabilitySlotCard } from './AvailabilitySlotCard';
import { AvailabilitySlotEditor } from './AvailabilitySlotEditor';

const DEFAULT_SLOT: AvailabilityFormSlotInput = {
  slotId: undefined,
  title: '',
  bucketType: AllocationBucketTypes.Monthly,
  bucketValue: 168,
  capabilities: [],
  workModel: AvailabilityWorkModels.Remote,
  workCity: null,
  workState: null,
  acceptsPJ: true,
  cltSalaryCeiling: null,
  candidates: [],
  chosenCollaboratorId: null,
  lastSuggestionAt: null,
  lastMatchFilters: null,
  lastMatchResultsCount: null,
  fulfillmentStrategy: 'known',
};

const DEFAULT_VALUES: AvailabilityFormValues = {
  clientId: '',
  clientName: '',
  clientLogo: null,
  initiative: '',
  status: AvailabilityStatuses.Simulation,
  needType: AvailabilityNeedTypes.BodyShop,
  referenceDate: null,
  notes: '',
  isInternalDemand: false,
  priorityLevel: null,
  slots: [DEFAULT_SLOT],
};

export interface AvailabilityFormProps {
  onSubmit: (values: AvailabilityFormValues) => Promise<void> | void;
  onCancel?: () => void;
  initialValues?: AvailabilityFormValues | null;
  isSubmitting?: boolean;
  availabilityId?: string;
}

export function AvailabilityForm({
  onSubmit,
  onCancel,
  initialValues = null,
  isSubmitting = false,
  availabilityId,
}: AvailabilityFormProps) {
  const formTitle = availabilityId ? 'Editar disponibilidade' : 'Nova disponibilidade';
  const submitLabel = availabilityId ? 'Salvar alterações' : 'Salvar disponibilidade';

  const methods = useForm<AvailabilityFormValues>({
    mode: 'onChange',
    defaultValues: initialValues ?? DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    formState: { isSubmitting: isFormSubmitting, isDirty },
  } = methods;

  const { fields, append, remove, update, insert } = useFieldArray({
    control: methods.control,
    name: 'slots',
  });

  const { warning } = useToast();
  const [activeSlotIndex, setActiveSlotIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (initialValues) {
      methods.reset(initialValues);
    }
  }, [initialValues, methods]);

  const referenceDateValue = methods.watch('referenceDate');
  const referenceDateFormatted = referenceDateValue
    ? format(referenceDateValue, 'dd/MM/yyyy')
    : '';

  const handleReferenceDateChange = (value: string) => {
    const parsed = parse(value, 'dd/MM/yyyy', new Date());
    if (Number.isNaN(parsed.getTime())) {
      methods.setValue('referenceDate', null, { shouldDirty: true });
    } else {
      methods.setValue('referenceDate', parsed, { shouldDirty: true });
    }
  };

  const handleAddSlot = () => {
    append({
      ...DEFAULT_SLOT,
      slotId: undefined,
      candidates: [],
      chosenCollaboratorId: null,
      lastSuggestionAt: null,
      lastMatchFilters: null,
      lastMatchResultsCount: null,
      fulfillmentStrategy: 'known',
    });
    setActiveSlotIndex(fields.length);
  };

  const handleDuplicateSlot = (index: number) => {
    const slotToDuplicate = methods.getValues(`slots.${index}`);
    if (!slotToDuplicate) {
      warning('Vaga não encontrada', 'Não foi possível duplicar esta vaga.');
      return;
    }

    const duplicated: AvailabilityFormSlotInput = {
      ...slotToDuplicate,
      slotId: undefined,
      candidates: [],
      chosenCollaboratorId: null,
      lastSuggestionAt: null,
      lastMatchFilters: null,
      lastMatchResultsCount: null,
      fulfillmentStrategy: slotToDuplicate.fulfillmentStrategy ?? 'known',
    };

    insert(index + 1, duplicated);
    setActiveSlotIndex(index + 1);
  };

  const handleRemoveSlot = (index: number) => {
    remove(index);
  };

  const handleSlotSummaryUpdate = (index: number, slot: AvailabilityFormSlotInput) => {
    update(index, slot);
  };

  const submitDisabled = isSubmitting || isFormSubmitting || (!isDirty && !initialValues);

  const handleFormSubmit = handleSubmit(async (values) => {
    await onSubmit(values);
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleFormSubmit} className="space-y-8 pb-16">
        <section className="bg-white shadow rounded-lg p-6 space-y-6">
          <header className="space-y-2">
            <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wide">
              Dados gerais
            </p>
            <h2 className="text-2xl font-bold text-gray-900">{formTitle}</h2>
          </header>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-3">
              <label htmlFor="availability-client" className="text-sm font-medium text-gray-700">
                Cliente <span className="text-red-500">*</span>
              </label>
              <ClientCombobox
                selectedId={methods.watch('clientId') || undefined}
                onChange={(client) => {
                  methods.setValue('clientId', client?.id ?? '', {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                  methods.setValue('clientName', client?.name ?? '', {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                  methods.setValue('clientLogo', client?.logoUrl ?? null, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />
            </div>

            <div className="space-y-3">
              <label htmlFor="availability-initiative" className="text-sm font-medium text-gray-700">
                Iniciativa <span className="text-red-500">*</span>
              </label>
              <input
                id="availability-initiative"
                type="text"
                className="input-primary w-full"
                placeholder="Ex: Nova Squad de Cartões"
                value={methods.watch('initiative') ?? ''}
                onChange={(event) =>
                  methods.setValue('initiative', event.target.value, {
                    shouldDirty: true,
                    shouldTouch: true,
                  })
                }
              />
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-2">
              <label htmlFor="availability-need-type" className="text-sm font-medium text-gray-700">
                Tipo de necessidade
              </label>
              <select
                id="availability-need-type"
                className="input-primary w-full"
                value={methods.watch('needType')}
                onChange={(event) =>
                  methods.setValue('needType', event.target.value as any, {
                    shouldDirty: true,
                    shouldTouch: true,
                  })
                }
              >
                {Object.keys(AvailabilityNeedTypes).map((key) => {
                  const typeKey = key as keyof typeof AvailabilityNeedTypes;
                  const value = AvailabilityNeedTypes[typeKey];
                  const meta = AVAILABILITY_NEED_TYPE_METADATA[value];
                  return (
                    <option key={value} value={value}>
                      {meta.label}
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-gray-500">
                {AVAILABILITY_NEED_TYPE_METADATA[methods.watch('needType')].description}
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="availability-status" className="text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="availability-status"
                className="input-primary w-full"
                value={methods.watch('status')}
                onChange={(event) =>
                  methods.setValue('status', event.target.value as any, {
                    shouldDirty: true,
                    shouldTouch: true,
                  })
                }
              >
                {Object.keys(AvailabilityStatuses).map((key) => {
                  const statusKey = key as keyof typeof AvailabilityStatuses;
                  const value = AvailabilityStatuses[statusKey];
                  const meta = AVAILABILITY_STATUS_METADATA[value];
                  return (
                    <option key={value} value={value}>
                      {meta.label}
                    </option>
                  );
                })}
              </select>
              <p className="text-xs text-gray-500">
                {AVAILABILITY_STATUS_METADATA[methods.watch('status')].description}
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="availability-reference-date" className="text-sm font-medium text-gray-700">
                Data de referência
              </label>
              <CustomDatePicker
                id="availability-reference-date"
                value={referenceDateFormatted}
                onChange={handleReferenceDateChange}
                placeholder="dd/mm/aaaa"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="availability-notes" className="text-sm font-medium text-gray-700">
              Observações gerais
            </label>
            <textarea
              id="availability-notes"
              className="input-primary w-full min-h-[80px]"
              placeholder="Anotações relevantes sobre a necessidade."
              value={methods.watch('notes') ?? ''}
              onChange={(event) =>
                methods.setValue('notes', event.target.value, {
                  shouldDirty: true,
                  shouldTouch: true,
                })
              }
            />
          </div>
        </section>

        <section className="space-y-6">
          <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Vagas</h3>
              <p className="text-sm text-gray-500">
                Configure cada vaga de disponibilidade com seus requisitos específicos.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                className="btn-primary"
                onClick={handleAddSlot}
              >
                Nova vaga
              </button>
            </div>
          </header>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {fields.map((field, index) => (
              <AvailabilitySlotCard
                key={field.id}
                index={index}
                slot={methods.watch(`slots.${index}`)}
                onEdit={() => setActiveSlotIndex(index)}
                onDuplicate={() => handleDuplicateSlot(index)}
                onRemove={() => handleRemoveSlot(index)}
                bucketMetadata={ALLOCATION_BUCKET_METADATA}
                workModelMetadata={AVAILABILITY_WORK_MODEL_METADATA}
                onSummaryChange={(slot) => handleSlotSummaryUpdate(index, slot)}
              />
            ))}
          </div>
        </section>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 shadow-inner py-4">
          <div className="container mx-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end px-4">
            {onCancel ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={onCancel}
                disabled={isSubmitting || isFormSubmitting}
              >
                Cancelar
              </button>
            ) : null}
            <button
              type="submit"
              className="btn-primary"
              disabled={submitDisabled}
            >
              {isSubmitting || isFormSubmitting ? 'Salvando...' : submitLabel}
            </button>
          </div>
        </div>
      </form>

      <AvailabilitySlotEditor
        open={activeSlotIndex !== null}
        slotIndex={activeSlotIndex}
        onClose={() => setActiveSlotIndex(null)}
        bucketMetadata={ALLOCATION_BUCKET_METADATA}
        availabilityId={availabilityId}
      />
    </FormProvider>
  );
}
